import { InputText } from 'component-library/index';
import React, { useState, memo, useRef, useLayoutEffect } from 'react';
import { useField } from '../../../providers/ModernAcquisitionProvider/useField';
import { TErrorType } from '../../../providers/ModernAcquisitionProvider/types';
import SlideDown from '../../SlideDown/SlideDown';
import { TReactParseable } from '../../../utils/general-types';
import SessionStorage from '../../../utils/SessionStorage';
import { PathUtils } from '@adobe/aem-spa-page-model-manager';
import useBriteVerify, { IPhoneResponse } from '../../../hooks/useBriteVerify';
import { generateFieldErrorMessage } from '../../../utils/error-message';

export interface IPhoneNumberFieldWithProviderProps {
    fieldKey: 'phoneNumber'
    name: 'Phone Number';
    label?: string;
    id?: string;
    helperText?: string;
    disabled?: boolean;
    onBlurCallback?: () => void;
    onFocusCallback?: () => void;
    disclaimerMessage?: TReactParseable;
    disclaimerClassName?: string;
};
type TValidationResult = false | IPhoneResponse | "skipped";
type TLastPhone = {
    value: string;
    result: boolean;
    validationResult?: TValidationResult
};

const MemoizedInputText = memo(InputText);

export const PhoneNumberFieldWithProvider = memo(function PhoneNumberFieldWithProvider(props: IPhoneNumberFieldWithProviderProps & React.HTMLAttributes<HTMLInputElement>, ...rest) {

    const { field, updateField } = useField(props.fieldKey);
    const { phoneValidationApi } = useBriteVerify();
    const [isFocused, setIsFocused] = useState(false);
    const [lastPhone, setLastPhone] = useState<TLastPhone>({ value: '', result: false });
    const [fieldErrorMessage, setFieldErrorMessage] = useState<string>(generateFieldErrorMessage('Phone Number', 'missingValue'));
    const [plist] = useState<Array<String>>(PathUtils.getMetaPropertyValue('cq:plist')?.split(',').map(phone => atob(phone)) ?? []);
    const inputRef = useRef<HTMLInputElement>(null);
    const cursorPositionRef = useRef<number | null>(null);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const cursorPosition = event.target.selectionStart ?? event.target.value.length;
        const formattedValue = event.target.value.replace(/\D/g, '');
        if (formattedValue.length > 10) {
            const previousValue = formatPhoneNumber(field.value);
            const previousCursor = Math.min(cursorPosition - 1, previousValue.length);
            event.target.value = previousValue;
            event.target.setSelectionRange(previousCursor, previousCursor);
            return;
        }
        cursorPositionRef.current = event.target.value.slice(0, cursorPosition).replace(/\D/g, '').length;
        if (field.value !== formattedValue) {
            updateField({ value: formattedValue, state: 'neutral', errorType: 'None' });
        }
    };

    const handleBlur = async (): Promise<void> => {
        const trimmedValue = field.value?.trim() ?? '';
        const isValid = trimmedValue.length < 1 ? false : await validatePhone(trimmedValue);
        setIsFocused(false);
        if (isValid) {
            updateField({ ...field, state: 'success', errorType: 'None' })
        } else {
            const error: TErrorType = trimmedValue.length === 0
                ? 'requiredField'
                : trimmedValue.length === 10
                    ? 'thirdParty'
                    : 'invalidInput';
            updateField({ ...field, state: 'error', errorType: error })
            if (trimmedValue.length < 1) {
                setFieldErrorMessage(generateFieldErrorMessage('Phone Number', 'missingValue'));
            }
        }
        props.onBlurCallback && props.onBlurCallback();
    };

    const handleFocus = (): void => {
        setIsFocused(true);
        if (field.state === 'success') {
            updateField({ ...field, state: 'neutral' })
        }
        props.onFocusCallback && props.onFocusCallback();
    };

    const formatPhoneNumber = (phone: string | undefined): string => {
        if (typeof phone !== 'string') {
            return '';
        }
        if (phone.length > 3 && phone.length <= 6) {
            return `${phone.slice(0, 3)}-${phone.slice(3)}`
        } else if (phone.length > 6) {
            return `${phone.slice(0, 3)}-${phone.slice(3, 6)}-${phone.slice(6)}`
        }
        return phone;
    };

    const validatePhone = async (value: string): Promise<boolean> => {
        if (value.length < 10 || plist?.includes(value)) {
            setFieldErrorMessage(generateFieldErrorMessage('Phone Number'));
            return false;
        }
        const isAuthor = ['4502', '3000'].some((entry) => window.location.port.includes(entry));
        if (
            (!SessionStorage.contains('adobeMode') && !isAuthor) ||
            (SessionStorage.contains('adobeMode') && SessionStorage.contains('usePhoneApi'))
        ) {
            if (value === lastPhone?.value) {
                if (lastPhone.validationResult) {
                    SessionStorage.set('bvrslt', btoa(JSON.stringify(lastPhone.validationResult)));
                    setFieldErrorMessage(generateFieldErrorMessage('Phone Number', 'missingValue'));
                } else {
                    setFieldErrorMessage(generateFieldErrorMessage('Phone Number', 'thirdParty', 'Number'));
                }
                setLastPhone({ value: value, result: lastPhone.result, validationResult: lastPhone.validationResult });
                return lastPhone.result;
            }
            const validationResult: TValidationResult = await phoneValidationApi(value);
            if (validationResult) {
                SessionStorage.set('bvrslt', btoa(JSON.stringify(validationResult)));
                setFieldErrorMessage(generateFieldErrorMessage('Phone Number', 'missingValue'));
            } else {
                setFieldErrorMessage(generateFieldErrorMessage('Phone Number', 'thirdParty', 'Number'));
            }

            setLastPhone({ value: value, result: !!validationResult, validationResult: validationResult });
            return !!validationResult;
        }
        setLastPhone({ value: value, result: true });
        return true;
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
        if (event.key !== 'Backspace' && event.key !== 'Delete') {
            return;
        }
        const start = event.currentTarget.selectionStart ?? 0;
        const end = event.currentTarget.selectionEnd ?? start;
        if (event.currentTarget.value.slice(start, end) === '-') {
            event.preventDefault();
            event.currentTarget.setSelectionRange(start, start);
            return;
        }

        const isBackspaceDash = event.key === 'Backspace' &&
            start === end &&
            event.currentTarget.value[start - 1] === '-';
        const isDeleteDash = event.key === 'Delete' &&
            start === end &&
            event.currentTarget.value[start] === '-';

        if (isBackspaceDash || isDeleteDash) {
            event.preventDefault();
            const dashPosition = isBackspaceDash ? start - 1 : start + 1;
            inputRef.current.setSelectionRange(dashPosition,dashPosition);
        }
    };

    useLayoutEffect(() => {        
        if (!inputRef.current || cursorPositionRef.current === null) {
            return;
        }
        let cursorPos = 0;
        let totalDigits = 0;
        while (cursorPos < inputRef.current.value.length && totalDigits < cursorPositionRef.current) {
            if (/\d/.test(inputRef.current.value[cursorPos])) {
                totalDigits++;
            }
            cursorPos++;
        };
        inputRef.current.setSelectionRange(cursorPos, cursorPos);
        cursorPositionRef.current = null;
    }, [field.value]);

    return (
        <div>
            <MemoizedInputText
                ref={inputRef}
                required
                name={props.name}
                label={props.label || props.name}
                helperText={props.helperText}
                id={props.id}
                value={formatPhoneNumber(field.value)}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onChange={handleChange}
                type='tel'
                inputMode='numeric'
                errorMessage={fieldErrorMessage}
                state={field.state}
                disabled={props.disabled}
                {...rest}
                onKeyDown={handleKeyDown}
            />
            {props.disclaimerMessage &&
                <SlideDown
                    open={isFocused}
                    className={props.disclaimerClassName}
                    tabIndex={-1}
                >
                    {props.disclaimerMessage}
                </SlideDown>
            }
        </div>
    )

});

export default PhoneNumberFieldWithProvider;